import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeToggle = ({ showLabels = true, size = 'default' }) => {
    const { theme, setTheme, isDark } = useTheme();

    const sizes = {
        sm: { button: 'w-8 h-8', icon: 'w-4 h-4' },
        default: { button: 'w-10 h-10', icon: 'w-5 h-5' },
        lg: { button: 'w-12 h-12', icon: 'w-6 h-6' }
    };

    const currentSize = sizes[size] || sizes.default;

    if (!showLabels) {
        // Simple toggle button
        return (
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className={`${currentSize.button} rounded-xl flex items-center justify-center transition-colors
                    ${isDark 
                        ? 'bg-muted hover:bg-white/20 text-yellow-400' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    }`}
                data-testid="theme-toggle-button"
                aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
                <motion.div
                    initial={false}
                    animate={{ rotate: isDark ? 0 : 180 }}
                    transition={{ duration: 0.3 }}
                >
                    {isDark ? (
                        <Moon className={currentSize.icon} />
                    ) : (
                        <Sun className={currentSize.icon} />
                    )}
                </motion.div>
            </motion.button>
        );
    }

    // Full selector with labels
    const options = [
        { value: 'light', icon: Sun, label: 'Clair' },
        { value: 'dark', icon: Moon, label: 'Sombre' },
    ];

    return (
        <div className="flex items-center gap-2 p-1 rounded-xl bg-muted/50" data-testid="theme-selector">
            {options.map(({ value, icon: Icon, label }) => (
                <button
                    key={value}
                    onClick={() => setTheme(value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                        ${theme === value
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                        }`}
                    data-testid={`theme-option-${value}`}
                >
                    <Icon className="w-4 h-4" />
                    <span>{label}</span>
                </button>
            ))}
        </div>
    );
};

// Compact theme switch for header/sidebar
export const ThemeSwitch = () => {
    const { isDark, toggleTheme } = useTheme();

    return (
        <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                isDark ? 'bg-slate-700' : 'bg-slate-200'
            }`}
            data-testid="theme-switch"
            aria-label={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
        >
            {/* Track icons */}
            <Sun className={`absolute left-1.5 top-1.5 w-4 h-4 transition-opacity ${
                isDark ? 'opacity-30' : 'opacity-0'
            } text-yellow-500`} />
            <Moon className={`absolute right-1.5 top-1.5 w-4 h-4 transition-opacity ${
                isDark ? 'opacity-0' : 'opacity-30'
            } text-slate-400`} />
            
            {/* Thumb */}
            <motion.div
                initial={false}
                animate={{ x: isDark ? 28 : 2 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className={`absolute top-1 w-5 h-5 rounded-full shadow-md flex items-center justify-center ${
                    isDark ? 'bg-slate-900' : 'bg-white'
                }`}
            >
                {isDark ? (
                    <Moon className="w-3 h-3 text-yellow-400" />
                ) : (
                    <Sun className="w-3 h-3 text-yellow-500" />
                )}
            </motion.div>
        </motion.button>
    );
};

export default ThemeToggle;
